'use client';

import { twMerge } from 'tailwind-merge';

import { useState } from 'react';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { TopSpeciesLinkCard } from '../../../../components/Cards/TopSpeciesLinkCard/TopSpeciesLinkCard';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { Heading } from '../../../../components/Heading/Heading';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { getEdgesFromConnection } from '@graphql/connections/getEdgesFromConnection';
import {
  FishingWaterTopSpeciesSummaryFields,
  FishingWaterTopSpeciesNodeFields,
} from '../../../../generated/graphql';
import { SUB_LINK_IDS } from '../../helpers/fishingWaterHelpers';
import { TopSpeciesCTA } from './components/TopSpeciesCTA';

interface IProps {
  fishingWaterId: string;
  fishingWaterName: string;
  topSpeciesSummary: FishingWaterTopSpeciesSummaryFields;
  className?: string;
}

export const TopSpeciesSection = ({
  fishingWaterId,
  fishingWaterName,
  topSpeciesSummary,
  className,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const headingText = `Top fish species at ${fishingWaterName}`;
  const speciesEdges = getEdgesFromConnection<
    FishingWaterTopSpeciesNodeFields,
    { node: FishingWaterTopSpeciesNodeFields | null }
  >(topSpeciesSummary);

  const handleCTAClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: fishingWaterId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={SUB_LINK_IDS.TOP_SPECIES}
      className={twMerge(
        'sm:bg-lake-fog md:from-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl md:bg-transparent md:bg-linear-to-b',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col">
        {speciesEdges.map(({ node }, index) => {
          const image = node.image;
          const imageUrl = image.url;
          const imageWidth = image.width;
          const imageHeight = image.height;

          return (
            <TopSpeciesLinkCard
              key={`species-${node._id}`}
              speciesId={node._id}
              name={node.displayName}
              className={`w-full`}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              clickEventPosition={index}
              clickEventSourceId={fishingWaterId}
              clickEventSource="most-caught-species"
            />
          );
        })}
        <Button
          size="sm"
          className="mt-2 p-2 md:hidden"
          variant="textPrimary"
          onClick={handleCTAClick}
        >
          See more species
        </Button>
      </div>
      <TopSpeciesCTA onCTAClick={handleCTAClick} />
      <AppDownloadModal
        appDownloadSource="TopSpeciesSection"
        appDownloadSourceId={fishingWaterId}
        modalHeader={`Download the Fishbrain app to see all species from  ${fishingWaterName}`}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
